import { IComponentController, injector, IPromise, IQService, IRootScopeService } from 'angular';
import { finalize, MonoTypeOperatorFunction, Observable, Subject, takeUntil, tap } from 'rxjs';

import { None, Option } from '@contact/upgrade';

/**
 * Operator to auto unsubscribe from stream when receive destroy signal
 * @param base component/service frontend
 */
function baseDestroyed(base: IComponentController): Observable<true> {
  const modifiedBase = base as { __destroy$?: Observable<true>; $onDestroy(): void };
  if (modifiedBase.__destroy$) {
    return modifiedBase.__destroy$;
  }
  const oldOnDestroy = (base as any).$onDestroy;
  const destroy$ = new Subject<true>();
  modifiedBase.$onDestroy = () => {
    if (oldOnDestroy) oldOnDestroy.apply(base);
    destroy$.next(true);
    destroy$.complete();
  };
  return (modifiedBase.__destroy$ = destroy$.asObservable());
}

export function ng1Destroy<T>(base: IComponentController): MonoTypeOperatorFunction<T> {
  return takeUntil(baseDestroyed(base));
}

export function destroy<T>(base: any): MonoTypeOperatorFunction<T> {
  return takeUntil(baseDestroyed(base));
}

export class RxUtil {
  static fromPromise<T>(promise: () => IPromise<T>, $rootScope: Option<IRootScopeService> = None): Observable<T> {
    return Observable.create(subscriber => {
      promise().then(
        success => {
          subscriber.next(success);
          subscriber.complete();
        },
        error => subscriber.error(error)
      );
      return () => {};
    }).pipe(finalize(() => $rootScope.filter(scope => !scope.$$phase).forEach(scope => scope.$apply())));
  }

  static toPromise<T>(ob$: Observable<T>, $q: IQService): IPromise<T> {
    let deferred = $q.defer<T>();
    ob$.subscribe(
      next => deferred.resolve(next),
      err => deferred.reject(err)
    );
    return deferred.promise;
  }

  static toPromiseEmited<T>(ob$: Observable<T>, $q: IQService): IPromise<T> {
    let deferred = $q.defer<T>();
    let hasEmit: boolean = false;
    ob$.subscribe(
      next => {
        hasEmit = true;
        deferred.resolve(next);
      },
      err => deferred.reject(err),
      () => {
        if (!hasEmit) deferred.reject('no emit');
      }
    );
    return deferred.promise;
  }
}

/**
 * Operator to auto log all data emitting by stream
 * @param base  component/service frontend
 * @param prefix describe stream observable
 */
export function ng1Debug<T>(base: { name: string }, prefix: string = ''): MonoTypeOperatorFunction<T> {
  const $injector = injector(['ng']);
  let $log = $injector.get('$log');
  return tap<T>(
    next => $log.debug(`[${base.name}/${prefix}] next - ${JSON.stringify(next)}`),
    err => $log.debug(`[${base.name}/${prefix}] error - ${JSON.stringify(err)}`),
    () => $log.debug(`[${base.name}/${prefix}] done.`)
  );
}
